<template>
    <div
        class="order-link-entry"
        :class="{ 'order-link-entry--mobile': ebkMobile }"
    >
        <div v-if="loading" class="order-link-entry__panel">
            <i class="el-icon-loading order-link-entry__icon"></i>
            <p class="order-link-entry__text">{{ statusText }}</p>
        </div>
        <div v-else class="order-link-entry__panel">
            <p class="order-link-entry__text order-link-entry__text--error">
                {{ errorText || "无法打开订单" }}
            </p>
            <el-button
                type="primary"
                :size="ebkMobile ? 'medium' : 'small'"
                class="order-link-entry__btn"
                @click="goOrderList"
            >
                返回订单列表
            </el-button>
        </div>

        <!-- 移动端：切换供应商用 el-dialog，与订单详情确认按钮一致，避免 MessageBox 关不掉 -->
        <el-dialog
            title="切换供应商"
            :visible.sync="switchDialogVisible"
            :width="switchDialogWidth"
            append-to-body
            :close-on-click-modal="true"
            :custom-class="switchDialogClass"
            center
            @closed="onSwitchDialogClosed"
        >
            <div class="od-action-dialog__body">
                <i class="el-icon-warning od-action-dialog__ico"></i>
                <p class="od-action-dialog__text">{{ switchDialogMessage }}</p>
            </div>
            <span slot="footer" class="od-action-dialog__footer">
                <el-button
                    class="od-action-dialog__btn"
                    :disabled="switchSubmitting"
                    @click="onSwitchDialogCancel"
                    >取消</el-button
                >
                <el-button
                    type="primary"
                    class="od-action-dialog__btn"
                    :loading="switchSubmitting"
                    @click="onSwitchDialogConfirm"
                    >确定</el-button
                >
            </span>
        </el-dialog>
    </div>
</template>

<script>
import qs from "qs";
import { Message, MessageBox } from "element-ui";
import mobileBreakpoint from "@/mixins/mobileBreakpoint";
import { getToken, setToken } from "@/assets/js/auth";
import {
    ORDER_LINK_ROUTE_NAME,
    clearPendingOrderLink,
    savePendingOrderLink,
} from "@/utils/orderDeepLink";

export default {
    name: "OrderLinkEntry",
    mixins: [mobileBreakpoint],
    data() {
        return {
            loading: true,
            statusText: "正在打开订单…",
            errorText: "",
            _running: false,
            pendingOId: "",
            linkCtx: null,
            switchDialogVisible: false,
            switchDialogMessage: "",
            switchSubmitting: false,
        };
    },
    computed: {
        switchDialogWidth() {
            return this.useMobileSwitchDialog() ? "92%" : "420px";
        },
        switchDialogClass() {
            return this.useMobileSwitchDialog()
                ? "od-order-action-dialog od-order-action-dialog--mobile ol-switch-dialog"
                : "od-order-action-dialog ol-switch-dialog";
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler() {
                this.startEntry();
            },
        },
    },
    beforeDestroy() {
        this.switchDialogVisible = false;
    },
    methods: {
        useMobileSwitchDialog() {
            if (typeof window === "undefined" || !window.matchMedia) {
                return false;
            }
            return window.matchMedia("(max-width: 992px)").matches;
        },
        getOId() {
            const q = this.$route.query.o_id;
            return q != null && q !== "" ? String(q) : "";
        },
        startEntry() {
            if (this._running) {
                return;
            }
            const oId = this.getOId();
            if (!oId) {
                Message.warning("缺少订单参数");
                this.$router.replace({ path: "/order" });
                return;
            }
            this.pendingOId = oId;

            const userRaw = getToken("userInfo");
            if (!userRaw) {
                savePendingOrderLink({
                    name: ORDER_LINK_ROUTE_NAME,
                    query: {
                        o_id: oId,
                        tu_id: this.$route.query.tu_id
                            ? String(this.$route.query.tu_id)
                            : "",
                    },
                });
                this.$router.replace({
                    path: "/login",
                    query: Object.assign({}, this.$route.query, {
                        o_id: oId,
                    }),
                });
                return;
            }

            this._running = true;
            this.loading = true;
            this.errorText = "";
            this.statusText = "正在打开订单…";
            this.linkCtx = null;

            const self = this;
            this.$axios
                .orderLinkContext(qs.stringify({ o_id: oId }))
                .then(function (ctx) {
                    if (!ctx) {
                        self.loading = false;
                        self.errorText = "订单校验失败，请稍后重试";
                        return;
                    }
                    self.handleLinkContext(ctx);
                })
                .catch(function () {
                    self.loading = false;
                    self.errorText = "订单校验失败，请稍后重试";
                })
                .finally(function () {
                    self._running = false;
                });
        },
        handleLinkContext(ctx) {
            if (!ctx || !ctx.tu_id) {
                this.loading = false;
                this.errorText = "无法识别订单所属供应商";
                return;
            }
            this.linkCtx = ctx;

            let userInfo = {};
            try {
                userInfo = JSON.parse(getToken("userInfo"));
            } catch (e) {
                this.loading = false;
                this.errorText = "登录状态无效";
                return;
            }

            const currentId =
                userInfo.id != null ? String(userInfo.id) : "";
            const targetId = String(ctx.tu_id);

            if (currentId === targetId) {
                this.goOrderDetail();
                return;
            }

            const supplierName = ctx.supplier_name || "目标供应商";
            const msg =
                "该订单属于【" +
                supplierName +
                "】，将切换到该供应商后查看订单详情。是否继续？";

            if (this.useMobileSwitchDialog()) {
                this.loading = false;
                this.switchDialogMessage = msg;
                this.switchDialogVisible = true;
                return;
            }

            const self = this;
            MessageBox.confirm(msg, "切换供应商", {
                confirmButtonText: "确定",
                cancelButtonText: "取消",
                type: "warning",
            })
                .then(function () {
                    return self.doSupplierSwitch(targetId, supplierName);
                })
                .then(function (ok) {
                    if (ok) {
                        self.goOrderDetail();
                    } else {
                        self.goOrderList();
                    }
                })
                .catch(function () {
                    self.goOrderList();
                });
        },
        onSwitchDialogConfirm() {
            if (!this.linkCtx || this.switchSubmitting) {
                return;
            }
            const targetId = String(this.linkCtx.tu_id);
            const supplierName = this.linkCtx.supplier_name || "目标供应商";
            const self = this;
            this.switchSubmitting = true;
            this.doSupplierSwitch(targetId, supplierName)
                .then(function (ok) {
                    self.switchSubmitting = false;
                    self.switchDialogVisible = false;
                    if (ok) {
                        self.goOrderDetail();
                    } else {
                        self.goOrderList();
                    }
                })
                .catch(function () {
                    self.switchSubmitting = false;
                });
        },
        onSwitchDialogCancel() {
            this.switchDialogVisible = false;
            this.goOrderList();
        },
        onSwitchDialogClosed() {
            this.switchSubmitting = false;
        },
        doSupplierSwitch(targetId, supplierName) {
            return this.$axios
                .supplierSwitchToTuId(qs.stringify({ tu_id: targetId }))
                .then(function (res) {
                    if (res && res.token) {
                        setToken("userInfo", JSON.stringify(res));
                        Message.success(
                            "已切换到「" + supplierName + "」"
                        );
                        return true;
                    }
                    Message.error("切换供应商失败");
                    return false;
                });
        },
        resetEntryState() {
            clearPendingOrderLink();
            this.linkCtx = null;
            this.pendingOId = "";
            this.switchDialogVisible = false;
            this.switchDialogMessage = "";
            this.switchSubmitting = false;
            this._running = false;
        },
        goOrderDetail() {
            const oId = this.pendingOId || this.getOId();
            if (!oId) {
                this.goOrderList();
                return;
            }
            this.resetEntryState();
            this.loading = false;
            this.$router.replace({
                name: "orderDetail",
                query: {
                    o_id: oId,
                    _: String(Date.now()),
                },
            });
        },
        goOrderList() {
            this.resetEntryState();
            this.loading = false;
            this.$router.replace({ path: "/order" });
        },
    },
};
</script>

<style scoped>
.order-link-entry {
    min-height: 100vh;
    min-height: 100dvh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f7fa;
    padding: max(24px, env(safe-area-inset-top, 0px)) 16px
        max(24px, env(safe-area-inset-bottom, 0px));
    box-sizing: border-box;
}
.order-link-entry__panel {
    text-align: center;
    width: 100%;
    max-width: 320px;
}
.order-link-entry__icon {
    font-size: 36px;
    color: #409eff;
}
.order-link-entry__text {
    margin: 16px 0 20px;
    font-size: 15px;
    color: #606266;
    line-height: 1.5;
    word-break: break-word;
}
.order-link-entry__text--error {
    color: #f56c6c;
}
.order-link-entry__btn {
    min-width: 140px;
}
.order-link-entry--mobile .order-link-entry__panel {
    max-width: none;
    padding: 0 8px;
}
.order-link-entry--mobile .order-link-entry__icon {
    font-size: 40px;
}
.order-link-entry--mobile .order-link-entry__text {
    font-size: 16px;
    margin: 20px 0 24px;
}
.order-link-entry--mobile .order-link-entry__btn {
    display: block;
    width: 100%;
    min-height: 44px;
    touch-action: manipulation;
}
</style>

<!-- 与 orderDetail 移动端确认 dialog 同款（append-to-body，须全局类名） -->
<style>
.od-order-action-dialog--mobile.el-dialog,
.ol-switch-dialog.od-order-action-dialog--mobile.el-dialog {
    margin-top: 8vh !important;
    border-radius: 12px;
    max-height: min(88vh, calc(100dvh - 32px));
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
}
.od-order-action-dialog--mobile .el-dialog__header,
.ol-switch-dialog.od-order-action-dialog--mobile .el-dialog__header {
    padding: 16px 16px 10px;
    box-sizing: border-box;
}
.od-order-action-dialog--mobile .el-dialog__title,
.ol-switch-dialog.od-order-action-dialog--mobile .el-dialog__title {
    font-size: 17px;
    font-weight: 600;
    line-height: 1.4;
    word-break: break-word;
}
.od-order-action-dialog--mobile .el-dialog__body,
.ol-switch-dialog.od-order-action-dialog--mobile .el-dialog__body {
    padding: 8px 16px 12px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    box-sizing: border-box;
}
.od-order-action-dialog--mobile .od-action-dialog__body,
.ol-switch-dialog.od-order-action-dialog--mobile .od-action-dialog__body {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}
.od-order-action-dialog--mobile .od-action-dialog__ico,
.ol-switch-dialog.od-order-action-dialog--mobile .od-action-dialog__ico {
    color: #e6a23c;
    font-size: 22px;
    flex-shrink: 0;
    margin-top: 2px;
}
.od-order-action-dialog--mobile .od-action-dialog__text,
.ol-switch-dialog.od-order-action-dialog--mobile .od-action-dialog__text {
    margin: 0;
    font-size: 15px;
    line-height: 1.55;
    color: #606266;
    word-break: break-word;
}
.od-order-action-dialog--mobile .el-dialog__footer,
.ol-switch-dialog.od-order-action-dialog--mobile .el-dialog__footer {
    padding: 12px 16px;
    padding-bottom: max(12px, env(safe-area-inset-bottom, 0px));
    border-top: 1px solid #ebeef5;
    box-sizing: border-box;
}
.od-order-action-dialog--mobile .od-action-dialog__footer,
.ol-switch-dialog.od-order-action-dialog--mobile .od-action-dialog__footer {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
}
.od-order-action-dialog--mobile .od-action-dialog__footer .el-button,
.ol-switch-dialog.od-order-action-dialog--mobile
    .od-action-dialog__footer
    .el-button {
    width: 100%;
    margin: 0 !important;
    min-height: 44px;
    touch-action: manipulation;
}
</style>



// WEBPACK FOOTER //
// src/components/OrderLinkEntry.vue